import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./common.css";

const FIELDS = [
  { key: "username", label: "USER ID",    type: "text",     prefix: ">_", placeholder: "Enter username",  maxLength: 30 },
  { key: "email",    label: "EMAIL ADDR", type: "email",    prefix: "@_", placeholder: "Enter email",     maxLength: 60 },
  { key: "password", label: "PASSWORD",   type: "password", prefix: "🔒", placeholder: "Enter password",  maxLength: 50 },
  { key: "pin",      label: "4-DIGIT PIN",type: "password", prefix: "##", placeholder: "_ _ _ _",         maxLength: 4  },
];

const validate = (form) => {
  if (!form.username)                      return "USERNAME REQUIRED";
  if (!form.email || !form.email.includes("@")) return "VALID EMAIL REQUIRED";
  if (!form.password || form.password.length < 4) return "PASSWORD MIN 4 CHARS";
  if (!form.pin || form.pin.length !== 4)  return "PIN MUST BE 4 DIGITS";
  return null;
};

export default function Register() {
  const [form, setForm] = useState({ username: "", email: "", password: "", pin: "" });
  const [focusField, setFocusField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const [step, setStep] = useState(0); // for staggered field reveal
  const cardRef = useRef(null);
  const nav = useNavigate();

  // Stagger field reveals
  useEffect(() => {
    const t = setInterval(() => setStep((s) => s + 1), 120);
    return () => clearInterval(t);
  }, []);

  // 3-D tilt
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const onMove = (e) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2))  / (r.width / 2);
      const dy = (e.clientY - (r.top  + r.height / 2)) / (r.height / 2);
      card.style.transform = `perspective(900px) rotateY(${dx * 5}deg) rotateX(${-dy * 5}deg) translateY(-4px)`;
    };
    const onLeave = () => { card.style.transform = "perspective(900px) rotateY(0) rotateX(0) translateY(0)"; };
    card.addEventListener("mousemove", onMove);
    card.addEventListener("mouseleave", onLeave);
    return () => { card.removeEventListener("mousemove", onMove); card.removeEventListener("mouseleave", onLeave); };
  }, []);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleChange = (key, value) => {
    setError("");
    if (key === "pin") value = value.replace(/\D/g, "").slice(0, 4);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") register(); };

  const register = async () => {
    const err = validate(form);
    if (err) { setError(err); triggerShake(); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post("https://atmsim-pip.onrender.com/api/register", form);
      setSuccess(true);
      cardRef.current?.classList.add("exit");
      setTimeout(() => nav("/"), 1200);
    } catch {
      setError("USERNAME OR EMAIL ALREADY EXISTS");
      triggerShake();
      setLoading(false);
    }
  };

  // PIN strength indicator
  const pinFilled = form.pin.length;

  return (
    <div className="atm-reg-page">
      <div className="scanlines" />
      <div className="grid-bg" />
      <div className="corner tl" /><div className="corner tr" />
      <div className="corner bl" /><div className="corner br" />

      <div ref={cardRef} className={`atm-card reg-card ${shake ? "shake" : ""}`}>

        {/* Header */}
        <div className="card-header">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="11" r="5" stroke="#00ff88" strokeWidth="1.5" />
              <path d="M6 26c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#00ff88" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="24" cy="8" r="3" fill="#003d1f" stroke="#00ff88" strokeWidth="1.2" />
              <path d="M23 8h2M24 7v2" stroke="#00ff88" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <div className="bank-name">NAPPS BANK</div>
            <div className="bank-sub">NEW ACCOUNT REGISTRATION</div>
          </div>
          <div className="header-dot" />
        </div>

        <div className="card-divider" />

        {/* Progress bar */}
        <div className="progress-row">
          <span className="progress-label">PROFILE COMPLETION</span>
          <span className="progress-pct">
            {Math.round((Object.values(form).filter(Boolean).length / 4) * 100)}%
          </span>
        </div>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${(Object.values(form).filter(Boolean).length / 4) * 100}%` }}
          />
        </div>

        {/* Fields */}
        <div className="fields">
          {FIELDS.map((f, i) => (
            <div
              key={f.key}
              className={`field-wrap ${focusField === f.key ? "focused" : ""} ${step > i ? "revealed" : "hidden-field"}`}
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="field-header-row">
                <label className="field-label">
                  <span className="label-bracket">[</span>
                  {f.label}
                  <span className="label-bracket">]</span>
                </label>
                {/* PIN dots */}
                {f.key === "pin" && (
                  <div className="pin-dots">
                    {[0,1,2,3].map((d) => (
                      <div key={d} className={`pin-dot ${d < pinFilled ? "filled" : ""}`} />
                    ))}
                  </div>
                )}
                {/* Field check */}
                {f.key !== "pin" && form[f.key] && (
                  <span className="field-check">✔</span>
                )}
              </div>

              <div className="field-inner">
                <span className="field-prefix">{f.prefix}</span>
                <input
                  className="atm-input"
                  type={f.type}
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  maxLength={f.maxLength}
                  autoComplete="off"
                  onFocus={() => setFocusField(f.key)}
                  onBlur={() => setFocusField(null)}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <div className="field-underline" />
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="error-bar">
            <span className="error-icon">⚠</span>
            <span>{error}</span>
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="success-bar">
            <span>✔</span>
            <span>ACCOUNT CREATED — REDIRECTING...</span>
          </div>
        )}

        {/* Buttons */}
        <div className="btn-group">
          <button
            className={`atm-btn primary ${loading ? "loading" : ""}`}
            onClick={register}
            disabled={loading || success}
          >
            {loading ? (
              <span className="loading-dots"><span /><span /><span /></span>
            ) : (
              <><span className="btn-arrow">▶</span>CREATE ACCOUNT</>
            )}
          </button>

          <button className="atm-btn secondary" onClick={() => nav("/")}>
            <span className="btn-arrow-left">◀</span>
            BACK TO LOGIN
          </button>
        </div>

        {/* Footer */}
        <div className="card-footer">
          <span>256-BIT ENCRYPTED</span>
          <span className="footer-sep">|</span>
          <span>PCI DSS COMPLIANT</span>
          <span className="footer-sep">|</span>
          <span>ISO 27001</span>
        </div>
      </div>
    </div>
  );
}